import type { ITemplateListItem } from '@/types/Template'

const TEMPLATES_SEGMENT = 'templates'

/**
 * Único construtor da rota da tela. Espelha `dispatchesPath`
 * (`src/lib/dispatches.ts`) de propósito, inclusive no nome do param: link
 * colado num chamado é conferível contra Disparos, Nova Conversa e Templates
 * sem tradução mental.
 *
 * A lista é do TENANT. Com `channelId`, o canal é **contexto de
 * `availability`** e recorte de tela (`?channel_id=<id>`) — não outra rota.
 */
export function templatesPath(channelId?: string): string {
  const base = `/chatbot/${TEMPLATES_SEGMENT}`
  return channelId ? `${base}?channel_id=${encodeURIComponent(channelId)}` : base
}

/** Labels PT pros valores da Business API; valor desconhecido exibe o cru. */
export const TEMPLATE_STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Aprovado',
  PENDING: 'Pendente',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desabilitado',
}

export const TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação',
}

/** Labels PT pros formatos de header da Business API. */
export const HEADER_FORMAT_LABELS: Record<string, string> = {
  TEXT: 'Texto',
  IMAGE: 'Imagem',
  VIDEO: 'Vídeo',
  DOCUMENT: 'Documento',
}

/** Tom semântico do badge de status — a cor mora no CSS. */
export function toneDeStatus(status: string): 'positive' | 'pending' | 'negative' | 'muted' {
  switch (status) {
    case 'APPROVED':
      return 'positive'
    case 'PENDING':
      return 'pending'
    case 'REJECTED':
    case 'DISABLED':
      return 'negative'
    default:
      return 'muted'
  }
}

/** Os 5 valores de `availability.blocked_by`, em PT. */
export const BLOQUEIO_LABELS: Record<string, string> = {
  archived: 'Arquivado',
  status: 'Não aprovado na Meta',
  inactive: 'Ausente no último sync',
  channel: 'Não habilitado neste canal',
  transmission_group: 'Não habilitado neste grupo',
}

/**
 * Prioridade de EXIBIÇÃO — definida aqui, não herdada do back.
 *
 * O back produz o array por `append` numa ordem sua (status → inactive →
 * archived → channel → group) e nenhum teste de lá a trava. Mais: a ordem dele
 * não é a útil. `archived` vem primeiro porque é a única razão que o operador
 * desfaz DESTA tela; `channel`/`transmission_group` vêm por último porque são
 * relativos ao contexto que a própria tela escolheu.
 */
const BLOQUEIO_PRIORIDADE = [
  'archived',
  'status',
  'inactive',
  'channel',
  'transmission_group',
] as const

function ordenaBloqueios(blockedBy: string[]): string[] {
  const conhecidos = BLOQUEIO_PRIORIDADE.filter((b) => blockedBy.includes(b)) as string[]
  // Valor fora do vocabulário não some: o back pode ganhar um sexto rótulo, e
  // engoli-lo faria a tela dizer "não enviável" sem dizer por quê.
  const desconhecidos = blockedBy.filter((b) => !BLOQUEIO_PRIORIDADE.includes(b as never))
  return [...conhecidos, ...desconhecidos]
}

/** Todos os motivos, já rotulados, na prioridade da tela. */
export function motivosDeBloqueio(blockedBy: string[]): string[] {
  return ordenaBloqueios(blockedBy).map((b) => BLOQUEIO_LABELS[b] ?? b)
}

/** O motivo que a linha exibe. `null` quando o template é enviável. */
export function motivoPrincipalDeBloqueio(blockedBy: string[]): string | null {
  return motivosDeBloqueio(blockedBy)[0] ?? null
}

/**
 * A linha some quando o CONTEXTO de canal a bloqueia — e só nesse caso.
 *
 * É o segundo tempo do recorte por canal (spec §3): `?channel_id=X` não remove
 * ninguém no back (`manager.py::_availability` só faz `append`), então quem
 * esconde é a tela. Esconder por qualquer outro motivo apagaria justamente os
 * templates que a tela existe pra mostrar (reprovado, inativo, arquivado).
 */
export function bloqueadoPorCanal(template: ITemplateListItem): boolean {
  return template.availability.blocked_by.includes('channel')
}

/** Tom semântico do chip. A cor mora no CSS, igual ao badge de status. */
export type TemplateBadgeTone = 'danger' | 'warn' | 'positive' | 'info' | 'neutral'

/** Ícone do chip. Nome semântico, não o do `lucide` — quem mapeia é o card. */
export type TemplateBadgeIcon = 'smartphone' | 'ios-autofill' | 'link-off'

export interface TemplateBadge {
  /** Estável e único no card: é a `key` do React e a âncora dos testes. */
  id: string
  /** Texto do chip. `null` = chip de ÍCONE só, e aí o texto vive no `title`. */
  label: string | null
  tone: TemplateBadgeTone
  /** Tooltip **e** nome acessível. Sempre presente, inclusive nos de texto. */
  title: string
  icon?: TemplateBadgeIcon
}

const QUALIDADE_LABELS: Record<string, string> = {
  YELLOW: 'Qualidade média',
  RED: 'Qualidade baixa',
}

const QUALIDADE_TONS: Record<string, TemplateBadgeTone> = {
  YELLOW: 'warn',
  RED: 'danger',
}

/**
 * Score que NÃO vira chip: é o estado esperado de um template saudável.
 *
 * ⚠️ Isto colapsa "avaliado como GREEN" com "nunca avaliado" — os dois somem da
 * tela. É troca deliberada: o chip responde "há algo fora do esperado?", não
 * "qual é a qualidade?", e a distinção entre bom e sem-dado não muda nenhuma
 * ação do operador. Se um dia a tela precisar responder a segunda pergunta, o
 * lugar é o detalhe do template, não o card da grade.
 */
const QUALIDADE_ESPERADA = 'GREEN'

/**
 * Os chips que o card exibe, na ordem em que aparecem.
 *
 * 🔴 **O card só afirma o que DESVIA do esperado.** Chip que aparece em todo
 * card não informa nada e ainda gasta a largura de que o próximo chip precisa:
 * é por isso que `GREEN` não vira chip, e por isso o `APPROVED` nem chega aqui
 * (some no card). O sinal está na AUSÊNCIA.
 *
 * 🔴 **E o que aparece, afirma só o positivo.** Nos três booleanos v26, `false`
 * é estado dito pela Meta e `null` é "a Meta não devolveu" — o back não colapsa
 * os dois, e aqui também não: nenhum dos dois gera chip. Ausência de badge não
 * significa "não"; significa que a tela não afirmou nada. Um chip "não entrega
 * só no aparelho principal" seria falso pro `null` e ruído pro `false`.
 *
 * A ORDEM é da tela, não do back: o bloqueio primeiro porque é o que impede o
 * envio, e os três de entrega por último porque são propriedade de detalhe, não
 * critério de listagem. Eles saem como ícone (`label: null`) porque seis chips
 * de texto numa linha fazem o card crescer de altura e desalinham a grade.
 *
 * Pura de propósito: o card só renderiza, e a regra fica testável sem DOM.
 */
export function badgesDoTemplate(template: ITemplateListItem): TemplateBadge[] {
  const badges: TemplateBadge[] = []

  const motivo = motivoPrincipalDeBloqueio(template.availability.blocked_by)
  if (motivo) {
    badges.push({
      id: 'bloqueio',
      label: motivo,
      tone: 'danger',
      // Os demais motivos ficam no tooltip, sem poluir o card.
      title: motivosDeBloqueio(template.availability.blocked_by).join(' · '),
    })
  }

  /* `score` preenchido, NUNCA `quality_score !== null`: `{}` no banco vira
     `{score: null, date: null}`, e o objeto presente com tudo nulo produziria
     um chip vazio. `date` não entra em nada — não é atributo do conteúdo. */
  const score = template.quality_score?.score
  if (score && score !== QUALIDADE_ESPERADA) {
    badges.push({
      id: 'qualidade',
      // Vocabulário aberto: o webhook grava a string que chegou. O valor
      // desconhecido sai cru, mas prefixado — "ROXO" sozinho não diz de quê.
      label: QUALIDADE_LABELS[score] ?? `Qualidade: ${score}`,
      tone: QUALIDADE_TONS[score] ?? 'neutral',
      title: `Qualidade do template na Meta: ${score}`,
    })
  }

  /* `has_variables` e `previous_category` NÃO viram chip. Os dois são fato de
     ficha técnica, não motivo pra parar a leitura da grade: nem impedem envio
     nem pedem ação. Ficam pro detalhe do template, quando ele existir. Se
     voltarem, os rótulos são "Exige parâmetros" (nunca "tem variáveis" — o
     campo é derivado de `components[].example`) e "Antes: <categoria PT>". */

  if (template.is_primary_device_delivery_only === true) {
    badges.push({
      id: 'aparelho-principal',
      label: null,
      tone: 'info',
      title: 'Só entrega no aparelho principal',
      icon: 'smartphone',
    })
  }

  if (template.disable_ios_autofill === true) {
    badges.push({
      id: 'ios-autofill',
      label: null,
      tone: 'info',
      title: 'Preenchimento automático do iOS desativado',
      icon: 'ios-autofill',
    })
  }

  if (template.cta_url_link_tracking_opted_out === true) {
    badges.push({
      id: 'sem-rastreio',
      label: null,
      tone: 'info',
      title: 'Sem rastreio nos links dos botões',
      icon: 'link-off',
    })
  }

  return badges
}

export type TemplateSort = 'name' | 'status'

/** `localeCompare` pt-BR: sem collation, `órgão` cai depois de `zap`. */
const porNome = (a: ITemplateListItem, b: ITemplateListItem) =>
  a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })

/**
 * Ordena uma CÓPIA. **A rota do back não ordena** — `find_in_scope` chama
 * `_find_many(query)` sem `sort`, então a ordem entre duas chamadas não é
 * estável e ordenar aqui deixou de ser cosmético.
 */
export function ordenaTemplates(
  templates: ITemplateListItem[],
  sort: TemplateSort,
): ITemplateListItem[] {
  return [...templates].sort((a, b) => {
    if (sort === 'status') {
      const porStatus = a.status.localeCompare(b.status, 'pt-BR', { sensitivity: 'base' })
      if (porStatus !== 0) return porStatus
    }
    return porNome(a, b)
  })
}
